import { ChangeEvent } from "react";

import { useTranslator } from "@eo-locale/react";

import { string } from "yup";

import {
  FormControl,
  FormHelperText,
  FormErrorMessage,
  FormErrorIcon,
  HTMLWrapper,
  FormLabel,
  Input,
  Flex,
  VisuallyHidden
} from "@/storybook-components";
import type { Question } from "@/types";
import { setAnswer } from "@/redux/features/questions/slice";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";

interface SingleLineQuestionProps {
  question: Question;
}

export const validate = async (
  answer: string,
  question: Question
): Promise<string[]> => {
  let schema = string();
  if (question.required) {
    schema = schema.required();
  }
  try {
    await schema.max(question.maxLength || 255).validate(answer);
    return [];
  } catch (err) {
    return err.errors;
  }
};

const SingleLineQuestion = ({
  question
}: SingleLineQuestionProps): JSX.Element => {
  const { translate } = useTranslator();
  const dispatch = useAppDispatch();
  const { answers, errors, status } = useAppSelector(
    (state) => state.questions
  );

  const error = errors[question.id] || "";
  const maxLength = question.maxLength || 255;
  const answer = answers[question.id] || "";

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    dispatch(
      setAnswer({
        questionId: question.id,
        answer: e.target.value
      })
    );
  };

  return (
    <FormControl
      my={4}
      id={`${question.id}`}
      isInvalid={error.length > 0}
      isRequired={question.required}
      isDisabled={status === "saving"}
      className={error.length > 0 ? "ehqQuestionError" : null}
    >
      <FormLabel>
        <HTMLWrapper html={question.question} />
        {question.required && (
          <VisuallyHidden>{`* ${translate("common.required")}`}</VisuallyHidden>
        )}
      </FormLabel>
      <Input
        type="text"
        value={answer}
        maxLength={maxLength}
        onChange={handleChange}
        size={{ base: "lg", lg: "md" }}
        data-testid="singleLineQuestion-input"
        name="singleLineQuestion-input"
        autoComplete="off"
      />
      <Flex justify="space-between">
        <div>
          {error.length > 0 && (
            <FormErrorMessage data-testid="singleLineQuestion-errorMessage">
              <FormErrorIcon />
              {translate(`errors.${error}`)}
            </FormErrorMessage>
          )}
          <FormHelperText>{question.notes}</FormHelperText>
        </div>
        <FormHelperText>{`${answer.length}/${maxLength}`}</FormHelperText>
      </Flex>
    </FormControl>
  );
};

export default SingleLineQuestion;
